import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import useReveal from "../hooks/useReveal";
import { useLang } from "../i18n/LanguageContext";
import { useFavorites } from "../context/FavoritesContext";
import { categoryLabel } from "../data/categories";
import { isFeatured } from "../hooks/useVendors";
import { isPremium } from "../hooks/usePlanSettings";
import { formatPrice } from "../lib/price";

export default function VendorCard({ vendor, index = 0 }) {
  const { t, lang } = useLang();
  const { isFavorite, toggle } = useFavorites();
  const [ref, shown] = useReveal();

  const saved = isFavorite(vendor.id);
  const featured = isFeatured(vendor);
  const premium = isPremium(vendor);
  const typeLabel = vendor.accountType === "company" ? t("card.company") : t("card.individual");
  const to = `/vendor/${vendor.id}`;
  const cover =
    vendor.photos && vendor.photos.length
      ? `url(${vendor.photos[0]}) center/cover`
      : `linear-gradient(140deg, ${(vendor.gradient && vendor.gradient[0]) || "#3a5a47"}, ${
          (vendor.gradient && vendor.gradient[1]) || "#2f4a3c"
        })`;

  return (
    <Card ref={ref} $shown={shown} $delay={(index % 3) * 0.08} $featured={featured}>
      <Media>
        {featured && <FeatBadge>★ {t("card.featured")}</FeatBadge>}
        <CoverLink to={to} style={{ background: cover }} aria-label={vendor.businessName}>
          <Tag>{categoryLabel(vendor.category, lang)}</Tag>
        </CoverLink>
        <Fav $saved={saved} onClick={() => toggle(vendor.id)} aria-label={saved ? "Saved" : "Save"}>
          <svg viewBox="0 0 24 24">
            <path d="M12 21s-7.5-4.6-9.8-9C.9 8.6 2.4 5 6 5c2 0 3.2 1.2 4 2.3C10.8 6.2 12 5 14 5c3.6 0 5.1 3.6 3.8 7-2.3 4.4-9.8 9-9.8 9z" />
          </svg>
        </Fav>
      </Media>
      <Body>
        <Meta>
          <span>{vendor.city}</span>
          <i />
          <span>{typeLabel}</span>
        </Meta>
        <TitleLink to={to}>
          <h3>{vendor.businessName}</h3>
        </TitleLink>
        <Chips>
          {formatPrice(vendor, lang === "sq") && <span className="price">{formatPrice(vendor, lang === "sq")}</span>}
          <span>{typeLabel}</span>
          {premium && <span className="prem">✓ {t("plan.premium")}</span>}
        </Chips>
        <Contact>
          <a className="call" href={`tel:${vendor.phone}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></svg>
            {t("card.call")}
          </a>
          <a className="wa" href={`https://wa.me/${vendor.whatsapp}`} target="_blank" rel="noreferrer">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 0-8.5 15.3L2 22l4.8-1.5A10 10 0 1 0 12 2zm0 18a8 8 0 0 1-4.1-1.1l-.3-.2-2.8.9.9-2.8-.2-.3A8 8 0 1 1 12 20zm4.5-5.9c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.6.8-.8 1-.3.2-.5.1a6.5 6.5 0 0 1-1.9-1.2 7.3 7.3 0 0 1-1.4-1.7c-.1-.3 0-.4.1-.5l.4-.5.3-.5v-.4l-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.6 4c.6.3 1.1.4 1.5.6.6.2 1.2.2 1.6.1.5-.1 1.5-.6 1.7-1.2s.2-1.1.2-1.2-.2-.2-.5-.3z" /></svg>
            {t("card.whatsapp")}
          </a>
          {premium && vendor.instagram && (
            <a className="ig" href={`https://instagram.com/${vendor.instagram}`} target="_blank" rel="noreferrer" aria-label="Instagram">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></svg>
            </a>
          )}
        </Contact>
      </Body>
    </Card>
  );
}

const Card = styled.article`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ $featured, theme }) => ($featured ? theme.colors.gold : theme.colors.hair)};
  box-shadow: ${({ $featured }) => ($featured ? "0 0 0 1px rgba(201,182,142,.5)" : "none")};
  border-radius: ${({ theme }) => theme.radius};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transform: translateY(${({ $shown }) => ($shown ? "0" : "26px")});
  transition: opacity 0.6s ${({ $delay }) => $delay}s,
    transform 0.6s cubic-bezier(.2,.7,.2,1) ${({ $delay }) => $delay}s,
    box-shadow 0.3s;
  &:hover {
    transform: translateY(-6px);
    box-shadow: ${({ theme }) => theme.shadow};
  }
`;

const Media = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
`;

const FeatBadge = styled.span`
  position: absolute;
  top: 13px;
  left: 13px;
  z-index: 2;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.greenDeep};
  background: ${({ theme }) => theme.colors.gold};
  padding: 5px 11px;
  border-radius: 100px;
`;

const CoverLink = styled(Link)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding: 14px;
`;

const Fav = styled.button`
  position: absolute;
  top: 13px;
  right: 13px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.92);
  display: grid;
  place-items: center;
  transition: 0.18s;
  &:hover { transform: scale(1.08); }
  svg {
    width: 18px;
    height: 18px;
    fill: ${({ $saved, theme }) => ($saved ? theme.colors.rose : "none")};
    stroke: ${({ $saved, theme }) => ($saved ? theme.colors.rose : theme.colors.greenDeep)};
    stroke-width: 1.8;
    transition: 0.18s;
  }
`;

const Tag = styled.span`
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.92);
  color: ${({ theme }) => theme.colors.greenDeep};
  padding: 5px 11px;
  border-radius: 100px;
`;

const Body = styled.div`
  padding: 18px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.inkSoft};
  i {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.gold};
  }
`;

const TitleLink = styled(Link)`
  margin-top: 2px;
  h3 {
    font-family: ${({ theme }) => theme.font.display};
    font-weight: 500;
    font-size: 21px;
    line-height: 1.15;
    transition: color 0.18s;
  }
  &:hover h3 { color: ${({ theme }) => theme.colors.green}; }
`;

const Chips = styled.div`
  display: flex;
  gap: 7px;
  margin-top: 4px;
  span {
    font-size: 11.5px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.inkSoft};
    background: ${({ theme }) => theme.colors.creamDeep};
    padding: 4px 10px;
    border-radius: 7px;
  }
  span.price { color: ${({ theme }) => theme.colors.green}; font-weight: 600; }
  span.prem { color: ${({ theme }) => theme.colors.greenDeep}; background: ${({ theme }) => theme.colors.gold}; font-weight: 600; }
`;

const Contact = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.hair};
  a {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    padding: 9px;
    border-radius: 10px;
    transition: 0.18s;
    svg { width: 16px; height: 16px; }
  }
  a.call { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; }
  a.call:hover { background: ${({ theme }) => theme.colors.greenDeep}; }
  a.wa { background: ${({ theme }) => theme.colors.creamDeep}; color: ${({ theme }) => theme.colors.ink}; }
  a.wa:hover { background: ${({ theme }) => theme.colors.roseSoft}; }
  a.ig { flex: 0 0 42px; background: ${({ theme }) => theme.colors.creamDeep}; color: ${({ theme }) => theme.colors.ink}; }
  a.ig:hover { background: ${({ theme }) => theme.colors.roseSoft}; }
`;
